import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  AuthGuard,
  CompanyGuard,
  LineCallback,
  AdminRoutes,
  InvitationAcceptance,
  useIsAdmin,
} from "@x-veloper/dataslot-auth";
import Dashboard from "./pages/Dashboard";
import AdminCommissionRates from "./pages/AdminCommissionRates";
import SelectCompany from "./pages/SelectCompany";
import SalesOverview from "./pages/SalesOverview";
import SalespersonAnalytics from "./pages/SalespersonAnalytics";
import CustomerAnalytics from "./pages/CustomerAnalytics";
import CustomerDetail from "./pages/CustomerDetail";
import CustomersOverview from "./pages/CustomersOverview";
import { RFMAnalysis } from "./pages/RFMAnalysis";
import ProductionOverview from "./pages/ProductionOverview";
import ProductionPerformance from "./pages/ProductionPerformance";
import ProductionMachines from "./pages/ProductionMachines";
import ProductionMachineDetail from "./pages/ProductionMachineDetail";
import ProductionPeople from "./pages/ProductionPeople";
import ProductionPersonDetail from "./pages/ProductionPersonDetail";
import Layout from "./components/Layout";
import DataslotAuthWrapper from "./components/DataslotAuthWrapper";
import { Toaster, ToastContextProvider } from "./components/ui/toaster";
import { ViewAsRoleProvider } from "./contexts/ViewAsRoleContext";
import { AuthDebug } from "./components/AuthDebug";
import { t } from "@/shared/lib/th-translations";
import { InvitationDebug } from "./components/InvitationDebug";

// Server-side auth service URL
const AUTH_SERVICE_URL = "https://auth2.firebase.dataslot.cc";

// Restricts a route subtree to SuperAdmin / Admin roles. Non-admins are
// redirected to the dashboard.
function AdminGuard({ children }: { children: React.ReactNode }) {
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AppContent() {
  const [processingToken, setProcessingToken] = useState(false);

  // Handle token from server-side auth service
  useEffect(() => {
    const handleAuthToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");

      if (token) {
        setProcessingToken(true);
        try {
          // Import Firebase auth dynamically and get auth from AuthProvider
          const { signInWithCustomToken } = await import("firebase/auth");
          const { getAuthInstance } = await import("@x-veloper/dataslot-auth");
          const auth = getAuthInstance();

          // Sign in to Firebase with custom token
          await signInWithCustomToken(auth, token);

          // Clean up URL (remove token from URL bar)
          window.history.replaceState({}, document.title, window.location.pathname);

          console.log("Signed in successfully from server-side auth!");
        } catch (error) {
          console.error("Sign-in failed:", error);
        } finally {
          setProcessingToken(false);
        }
      }
    };

    handleAuthToken();
  }, []);

  // Server-side sign-in URL (for AuthGuard redirectTo)
  const getSignInUrl = () => {
    // Use origin + pathname to avoid capturing query params (like ?token=...)
    // This ensures clean redirect URL without auth tokens or previous return_url params
    const cleanUrl = `${window.location.origin}${window.location.pathname}`;
    const returnUrl = encodeURIComponent(cleanUrl);
    return `${AUTH_SERVICE_URL}/auth/signin?return_url=${returnUrl}`;
  };

  // Show loading screen while processing authentication token
  if (processingToken) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 text-sm">{t("auth.signingIn")}</p>
        </div>
      </div>
    );
  }

  return (
    <ToastContextProvider>
      <div className="App h-full">
        <Routes>
          {/* Public invitation acceptance route */}
          <Route path="/invitation/accept/:module/:company" element={<><AuthDebug /><InvitationDebug /><InvitationAcceptance /></>} />

          {/* Auth callback route for LINE (legacy support) */}
          <Route path="/auth/line/callback" element={<LineCallback redirectTo="/" />} />

          {/* Company selection route (requires auth but not company) */}
          <Route
            path="/select-company"
            element={
              <AuthGuard redirectTo={getSignInUrl()}>
                <SelectCompany />
              </AuthGuard>
            }
          />

          {/* Protected routes (requires auth AND company) */}
          <Route
            path="/*"
            element={
              <AuthGuard redirectTo={getSignInUrl()}>
                <CompanyGuard>
                  <ViewAsRoleProvider>
                    <DataslotAuthWrapper>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/admin/commission-rates" element={<AdminCommissionRates />} />

                          {/* Admin Routes (User Management, etc.) */}
                          <Route path="/admin/*" element={<AdminRoutes />} />

                          {/* Sales Analytics Routes */}
                          <Route path="/analytics/overview" element={<SalesOverview />} />
                          <Route path="/analytics/salespersons/:id" element={<SalespersonAnalytics />} />
                          <Route path="/analytics/salespersons/:id/customers" element={<CustomerAnalytics />} />
                          <Route path="/analytics/customers" element={<CustomersOverview />} />
                          <Route path="/analytics/customers/:customerId" element={<CustomerDetail />} />
                          <Route path="/analytics/rfm" element={<RFMAnalysis />} />

                          {/* Production Routes — SuperAdmin / Admin only */}
                          <Route path="/production/overview" element={<AdminGuard><ProductionOverview /></AdminGuard>} />
                          <Route path="/production/performance" element={<AdminGuard><ProductionPerformance /></AdminGuard>} />
                          <Route path="/production/machines" element={<AdminGuard><ProductionMachines /></AdminGuard>} />
                          <Route path="/production/machines/:machine" element={<AdminGuard><ProductionMachineDetail /></AdminGuard>} />
                          <Route path="/production/people" element={<AdminGuard><ProductionPeople /></AdminGuard>} />
                          <Route path="/production/people/:employeeId" element={<AdminGuard><ProductionPersonDetail /></AdminGuard>} />

                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                      </Layout>
                    </DataslotAuthWrapper>
                  </ViewAsRoleProvider>
                </CompanyGuard>
              </AuthGuard>
            }
          />
        </Routes>
        <Toaster />
      </div>
    </ToastContextProvider>
  );
}

// Wrapper to provide necessary context for AppContent
function App() {
  return <AppContent />;
}

export default App;